import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Avatar,
  alpha,
  Stack,
  useTheme,
  CircularProgress,
  IconButton,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import {
  Assignment as ContractIcon,
  Person as PersonIcon,
  Wifi as WifiIcon,
  Phone as PhoneIcon,
  Tv as TvIcon,
  Build as BuildIcon,
  LockOpen as LockOpenIcon,
  CancelOutlined as CancelOutlinedIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import { ContratoServicos } from '../../services/contratoServicosService';
import { formatarData, formatarValor } from '../../utils/formatToView';
import { usePermissions } from '../../contexts/AuthContext';

export interface ServicosTableProps {
  servicos: ContratoServicos[];
  totalRegistros?: number;
  loading?: boolean;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
  onCancelService?: (servico: ContratoServicos) => void;
  onRepairConnection?: (servico: ContratoServicos) => void;
  onLiberarMac?: (servico: ContratoServicos) => void;
}

const statusConfig: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }
> = {
  ativo: { label: 'Ativo', color: 'success' },
  bloqueado: { label: 'Bloqueado', color: 'warning' },
  cancelado: { label: 'Cancelado', color: 'error' },
  aguardando_instalacao: { label: 'Ag. Instalação', color: 'info' },
  aguardando_assinatura: { label: 'Ag. Assinatura', color: 'default' },
  suspenso: { label: 'Suspenso', color: 'warning' },
};

const ServicosTable: React.FC<ServicosTableProps> = ({
  servicos,
  totalRegistros,
  loading = false,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selectedIds = [],
  onSelectionChange,
  onCancelService,
  onRepairConnection,
  onLiberarMac,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { can } = usePermissions();

  const podeEditar = can('contratos.editar');

  // Menu de Ações Individual
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedServico, setSelectedServico] = useState<ContratoServicos | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, servico: ContratoServicos) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedServico(servico);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedServico(null);
  };

  // Seleção de Checkboxes
  const allIds = servicos.map((s) => s.id);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const isSomeSelected = allIds.some((id) => selectedIds.includes(id)) && !isAllSelected;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      const newSelected = Array.from(new Set([...selectedIds, ...allIds]));
      onSelectionChange(newSelected);
    } else {
      const newSelected = selectedIds.filter((id) => !allIds.includes(id));
      onSelectionChange(newSelected);
    }
  };

  const handleSelectOne = (id: number) => {
    if (!onSelectionChange) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const renderTipoChip = (tipo: string) => {
    switch (tipo) {
      case 'internet':
        return (
          <Chip
            size="small"
            icon={<WifiIcon sx={{ fontSize: '14px !important' }} />}
            label="Internet"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700, borderRadius: 1.5 }}
          />
        );
      case 'telefonia':
        return (
          <Chip
            size="small"
            icon={<PhoneIcon sx={{ fontSize: '14px !important' }} />}
            label="Telefonia"
            color="secondary"
            variant="outlined"
            sx={{ fontWeight: 700, borderRadius: 1.5 }}
          />
        );
      case 'tv':
        return (
          <Chip
            size="small"
            icon={<TvIcon sx={{ fontSize: '14px !important' }} />}
            label="TV"
            color="info"
            variant="outlined"
            sx={{ fontWeight: 700, borderRadius: 1.5 }}
          />
        );
      default:
        return <Chip size="small" label={tipo} variant="outlined" />;
    }
  };

  const totalCount = totalRegistros !== undefined ? totalRegistros : servicos.length;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <TableContainer>
        <Table size="medium">
          <TableHead
            sx={{
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.primary.main, 0.04),
              '& th': {
                fontWeight: 800,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'text.secondary',
                py: 1.5,
              },
            }}
          >
            <TableRow>
              {onSelectionChange && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              <TableCell>ID</TableCell>
              <TableCell>Cliente / Contrato</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Plano / Valor</TableCell>
              <TableCell>Conexão / PPPoE / IP</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registro / Instalação</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontWeight: 600 }}>
                    Carregando serviços...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : servicos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" fontWeight={700} color="text.secondary">
                    Nenhum serviço encontrado
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tente ajustar os filtros de busca.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              servicos.map((servico) => {
                const isSelected = selectedIds.includes(servico.id);
                const statusInfo = statusConfig[servico.status] || {
                  label: servico.status,
                  color: 'default',
                };
                const cliente = servico.contrato?.cliente;

                return (
                  <TableRow
                    key={servico.id}
                    hover
                    selected={isSelected}
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td, &:last-child th': { border: 0 },
                      transition: 'background-color 0.15s',
                    }}
                    onClick={() => {
                      if (servico.contrato_id) {
                        navigate(`/contracts/${servico.contrato_id}`);
                      }
                    }}
                  >
                    {onSelectionChange && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleSelectOne(servico.id)}
                        />
                      </TableCell>
                    )}

                    {/* ID */}
                    <TableCell sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                      #{servico.id}
                    </TableCell>

                    {/* Cliente / Contrato */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 34,
                            height: 34,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                          }}
                        >
                          {cliente?.nome ? cliente.nome.charAt(0).toUpperCase() : <PersonIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.2,
                              '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cliente?.id) navigate(`/clients/${cliente.id}`);
                            }}
                          >
                            {cliente?.nome || 'Cliente não associado'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.72rem' }}>
                            {servico.contrato?.titulo || `Contrato #${servico.contrato_id}`}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Tipo */}
                    <TableCell>{renderTipoChip(servico.tipo)}</TableCell>

                    {/* Plano / Valor */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem' }}>
                        {servico.plano?.nome || 'Sem plano'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {servico.valor ? formatarValor(servico.valor) : '---'}
                      </Typography>
                    </TableCell>

                    {/* Conexão / PPPoE / IP */}
                    <TableCell>
                      {servico.tipo === 'internet' ? (
                        <Stack spacing={0.5}>
                          {servico.conexao && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: servico.conexao === 'Online' ? 'success.main' : 'error.main',
                                  boxShadow: servico.conexao === 'Online' ? `0 0 6px ${theme.palette.success.main}` : 'none',
                                }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={800}
                                sx={{
                                  color: servico.conexao === 'Online' ? 'success.main' : 'error.main',
                                  fontSize: '0.72rem',
                                }}
                              >
                                {servico.conexao}
                              </Typography>
                            </Box>
                          )}
                          {servico.usuario_pppoe && (
                            <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              PPPoE: {servico.usuario_pppoe}
                            </Typography>
                          )}
                          {servico.endereco_ip && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                              IP: {servico.endereco_ip}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={statusInfo.label}
                        color={statusInfo.color}
                        size="small"
                        sx={{ fontWeight: 700, borderRadius: 1.5, fontSize: '0.72rem' }}
                      />
                    </TableCell>

                    {/* Registro / Instalação */}
                    <TableCell>
                      <Typography variant="caption" color="text.primary" display="block" fontWeight={600}>
                        Reg: {formatarData(servico.data_registro)}
                      </Typography>
                      {servico.data_instalacao && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                          Inst: {formatarData(servico.data_instalacao)}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Ações */}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {servico.tipo === 'internet' && podeEditar && onRepairConnection && (
                          <Tooltip title="Reparar Conexão">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => onRepairConnection(servico)}
                            >
                              <BuildIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, servico)}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu de Ações de Linha */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            if (selectedServico?.contrato_id) {
              navigate(`/contracts/${selectedServico.contrato_id}`);
            }
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <ContractIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Ver Contrato" />
        </MenuItem>

        {selectedServico?.tipo === 'internet' && podeEditar && onRepairConnection && (
          <MenuItem
            onClick={() => {
              if (selectedServico) onRepairConnection(selectedServico);
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <BuildIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Reparar Conexão" />
          </MenuItem>
        )}

        {selectedServico?.tipo === 'internet' && podeEditar && onLiberarMac && (
          <MenuItem
            onClick={() => {
              if (selectedServico) onLiberarMac(selectedServico);
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <LockOpenIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary="Liberar MAC" />
          </MenuItem>
        )}

        {podeEditar && onCancelService && selectedServico?.status !== 'cancelado' && (
          <MenuItem
            onClick={() => {
              if (selectedServico) onCancelService(selectedServico);
              handleCloseMenu();
            }}
            sx={{ color: 'warning.main' }}
          >
            <ListItemIcon>
              <CancelOutlinedIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary="Cancelar Serviço" />
          </MenuItem>
        )}
      </Menu>

      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[15, 30, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
      />
    </Paper>
  );
};

export default ServicosTable;
